import styled, { css } from 'styled-components'
import {
	FormikApiStatus,
	FormikFormStatus,
	FormikSubmitHandler,
	getError,
} from '@/components/Formik'
import { Formik, FormikValues } from 'formik'
import React, { MutableRefObject } from 'react'
import * as Yup from 'yup'
import { Form, FormItem, Input, SubmitButton } from 'formik-antd'
import { Col, Row } from 'antd'
import { themeGet } from '@styled-system/theme-get'
import { suffix } from '@/components/Suffix'

const StyledForm = styled(Form)`
	.text_r {
		font-size: 24px;
		font-weight: bold;
		color: ${themeGet('text.0', '#1B1B29')};
		margin-bottom: 36px;

		@media (max-width: 1200px) {
			font-size: 20px;
			margin-bottom: 24px;
		}
		@media (max-width: 768px) {
			font-size: 18px;
			margin-bottom: 16px;
		}
	}
`

const StyledFormItem = styled(FormItem)`
	margin-bottom: 48px;

	@media (max-width: 1200px) {
		margin-bottom: 36px;
	}
	@media (max-width: 768px) {
		margin-bottom: 24px;
	}

	.ant-row {
		.ant-col {
			.ant-form-item-control-input {
				.ant-form-item-control-input-content {
					.ant-input-affix-wrapper {
						padding: 0;
						border: 0;
						border-radius: 0;
						border-bottom: solid 2px #1b1b29;

						&.ant-input-affix-wrapper-focused {
							border-color: ${themeGet('primary.0', '#002FA8')};
						}
					}

					.ant-input {
						font-size: 24px;
						font-weight: bold;
						color: ${themeGet('text.0', '#1B1B29')};
						text-align: center;
						padding: 16px 0;
						border: 0;
					}

					@media (max-width: 1200px) {
						.ant-input {
							font-size: 20px;
							padding: 8px 0;
						}
					}
					@media (max-width: 768px) {
						.ant-input {
							font-size: 16px;
						}
					}
				}
			}
		}
	}
`

const StyledSubmitButton = styled(SubmitButton)<{ clickable: boolean }>`
	display: block;
	width: 384px;
	height: 56px;
	padding: 0;
	margin: 0 auto;
	box-shadow: 0 4px 4px 0 rgba(27, 27, 41, 0.3);
	border-radius: 0;
	${(props) =>
		props.clickable
			? css`
					background: #002fa8 !important;

					&:hover {
						background: rgba(0, 47, 168, 0.9) !important;
					}
			  `
			: css`
					background-color: rgba(27, 27, 41, 0.3) !important;
					pointer-events: none;
			  `};

	span {
		display: block;
		font-size: 24px;
		font-weight: bold;
		color: #fff;
		line-height: 54px;
	}

	@media (max-width: 1200px) {
		width: 200px;

		span {
			font-size: 20px;
		}
	}
	@media (max-width: 768px) {
		span {
			font-size: 18px;
		}
	}
`

export type TrackingNumberFormInitValues = FormikFormStatus<
	TrackingNumberFormDataProps,
	FormikApiStatus
>

export interface TrackingNumberFormDataProps {
	number1: string
	number2: string
	number3: string
	number4: string
}

export type TrackingNumberFormSubmit = FormikSubmitHandler<TrackingNumberFormDataProps>

export type TrackingNumberFormProps = TrackingNumberFormInitValues & {
	name: string
	onSubmit: TrackingNumberFormSubmit
	forwardRef?: MutableRefObject<FormikValues>
	scrollToInput?: (reactNode: any) => void
	title: string
}

export const TrackingNumberFormSchema = Yup.object().shape({
	// number1: Yup.string().length(4, '4자리를 입력해주세요.'),
	// number2: Yup.string().length(4, '4자리를 입력해주세요.'),
	// number3: Yup.string().length(5, '4자리를 입력해주세요.'),
	// number4: Yup.string().length(4, '4자리를 입력해주세요.'),
})

const TrackingNumberFormWrapper = styled.div``

export const TrackingNumberForm: React.FC<TrackingNumberFormProps> = ({
	name,
	onSubmit,
	forwardRef,
	initialStatus,
	initialValues,
	title,
}) => {
	return (
		<TrackingNumberFormWrapper>
			<Formik<TrackingNumberFormDataProps>
				innerRef={(instance) => {
					if (forwardRef) {
						forwardRef.current = instance
					}
				}}
				validationSchema={TrackingNumberFormSchema}
				onSubmit={onSubmit}
				initialValues={initialValues}
				initialStatus={initialStatus}>
				{({
					handleChange,
					handleBlur,
					touched,
					errors,
					isValid,
					handleSubmit,
					setValues,
					values,
					isSubmitting,
					status,
				}) => {
					const emailError = getError('email', { touched, errors, status })
					const descriptionError = getError('description', {
						touched,
						errors,
						status,
					})
					console.log(isValid)
					return (
						<StyledForm layout={'vertical'}>
							<p className={'text_r'}>{title}</p>
							<Row>
								<Col>
									<StyledFormItem name="number1" required={true}>
										<Input
											onChange={handleChange('number1')}
											autoFocus={true}
											onBlur={handleBlur('number1')}
											name="number1"
											placeholder="2204"
											maxLength={4}
											suffix={suffix}
										/>
									</StyledFormItem>
								</Col>
								<Col>
									{/*<StyledFormItem hasFeedback={false} name="number2">*/}
									<StyledFormItem name="number2" required={true}>
										<Input
											onChange={handleChange('number2')}
											autoFocus={true}
											onBlur={handleBlur('number2')}
											name="number2"
											placeholder="NETB"
											maxLength={4}
											suffix={suffix}
										/>
									</StyledFormItem>
								</Col>
								<Col>
									<StyledFormItem name="number3" required={true}>
										<Input
											onChange={handleChange('number3')}
											autoFocus={true}
											onBlur={handleBlur('number3')}
											name="number3"
											placeholder="EN009"
											maxLength={5}
											suffix={suffix}
										/>
									</StyledFormItem>
								</Col>
								<Col>
									<StyledFormItem name="number4" required={true}>
										<Input
											onChange={handleChange('number4')}
											autoFocus={true}
											onBlur={handleBlur('number4')}
											name="number4"
											placeholder="0710"
											maxLength={4}
											suffix={suffix}
										/>
									</StyledFormItem>
								</Col>
							</Row>
							<StyledSubmitButton
								disabled={!isValid || isSubmitting}
								onSubmit={handleSubmit}
								loading={isSubmitting}
								clickable={
									values.number1.length === 4 &&
									values.number2.length === 4 &&
									values.number3.length === 5 &&
									values.number4.length === 4
								}>
								조회
							</StyledSubmitButton>
						</StyledForm>
					)
				}}
			</Formik>
		</TrackingNumberFormWrapper>
	)
}
